import { FlagOutlined } from '@ant-design/icons'
import { Alert, Button, Form, Modal, Radio, notification } from 'antd'
import { useForm } from 'antd/es/form/Form'
import dayjs from 'dayjs'
import { FC, useEffect, useState } from 'react'
import useErrorModal from '../../../../../hooks/api/use-error-modal'
import { userStorage } from '../../../../../services/user-storage'
import { useUpdateRewindJobExecutorMutation } from '../../../../../store/api/rewind-job-executors-api'
import { useUpdateRewindJobMutation } from '../../../../../store/api/rewind-jobs-api'
import {
    rewindQueueEntriesApi,
    useDeleteRewindQueueEntryMutation,
} from '../../../../../store/api/rewind-queue-entries-api'
import { useAppDispatch } from '../../../../../store/store'
import { Reasons } from '../../../../../types/api/print-job-executor'
import { RewindJobStatuses } from '../../../../../types/api/rewind-job'
import RewindQueueEntry from '../../../../../types/api/rewind-queue-entry'
import {
    getCurrentUserActiveExecutor,
    isCurrentUserActiveExecutor,
} from '../helpers/rewind-job-helpers'

interface FinishRewindProps {
    rewindQueueEntry: RewindQueueEntry
}

const FinishRewind: FC<FinishRewindProps> = ({ rewindQueueEntry }) => {
    const [open, setOpen] = useState<boolean>(false)
    const [form] = useForm<{ status: Reasons }>()
    const [updateRewindJobExecutor, updateRewindJobExecutorState] =
        useUpdateRewindJobExecutorMutation()
    const [updateRewindJob, updateRewindJobState] = useUpdateRewindJobMutation()
    const [deleteRewindQueueEntry, deleteRewindQueueEntryState] = useDeleteRewindQueueEntryMutation()
    const dispatch = useAppDispatch()
    const currentUserDocumentId = userStorage.get()?.documentId
    const rewindJob = rewindQueueEntry.rewind_job
    const disabled = !isCurrentUserActiveExecutor(rewindJob, currentUserDocumentId)
    const isLoading =
        updateRewindJobExecutorState.isLoading ||
        updateRewindJobState.isLoading ||
        deleteRewindQueueEntryState.isLoading

    const onFinishRewind = () => {
        setOpen(true)
    }

    const onUpdateRewindJobExecutor = async (reason: Reasons) => {
        const executor = getCurrentUserActiveExecutor(rewindJob, currentUserDocumentId)
        if (!executor) return

        return updateRewindJobExecutor({
            docId: executor.documentId,
            body: {
                data: {
                    end: dayjs().toISOString(),
                    reason,
                },
            },
        })
    }

    const closeShift = async () => {
        const result = await onUpdateRewindJobExecutor(Reasons.ClosingShift)
        if (result?.data) {
            notification.success({ message: 'Смена закрыта' })
        }
    }

    const done = async () => {
        const result = await onUpdateRewindJobExecutor(Reasons.Done)
        if (result?.data && rewindJob?.documentId) {
            await updateRewindJob({
                docId: rewindJob.documentId,
                body: {
                    data: {
                        jobStatus: RewindJobStatuses.Completed,
                    },
                },
            }).unwrap()
            await deleteRewindQueueEntry(rewindQueueEntry.documentId)
        }
    }

    const onConfirm = async () => {
        try {
            await form.validateFields()
            const values = form.getFieldsValue()

            if (values.status === Reasons.ClosingShift) {
                await closeShift()
            } else if (values.status === Reasons.Done) {
                await done()
            }
        } catch (e) {
            console.error(e)
        }
    }

    useEffect(() => {
        if (deleteRewindQueueEntryState.isSuccess || updateRewindJobExecutorState.isSuccess) {
            dispatch(rewindQueueEntriesApi.util.invalidateTags(['RewindQueueEntries']))
            setOpen(false)
        }
    }, [deleteRewindQueueEntryState.isSuccess, updateRewindJobExecutorState.isSuccess, dispatch])

    useErrorModal(updateRewindJobExecutorState, 'Ошибка обновления исполнителя задания')
    useErrorModal(updateRewindJobState, 'Ошибка обновления задания перемотки')
    useErrorModal(deleteRewindQueueEntryState, 'Ошибка удаления задания из очереди')

    return (
        <>
            <Button
                icon={<FlagOutlined />}
                onClick={onFinishRewind}
                disabled={disabled}
                loading={isLoading}
            >
                Завершить
            </Button>
            <Modal
                open={open}
                onOk={onConfirm}
                confirmLoading={isLoading}
                onCancel={() => setOpen(false)}
            >
                <Form form={form}>
                    <Form.Item
                        name='status'
                        rules={[
                            {
                                required: true,
                                message: 'Пожалуйста, укажите причину завершения перемотки',
                            },
                        ]}
                    >
                        <Radio.Group
                            optionType='button'
                            options={[
                                { value: Reasons.Done, label: Reasons.Done },
                                { value: Reasons.ClosingShift, label: Reasons.ClosingShift },
                            ]}
                        />
                    </Form.Item>
                    <Alert
                        type='info'
                        message={
                            <>
                                Не забудьте отправить фотоотчет с номером задания{' '}
                                <b>{rewindQueueEntry.rewind_job?.id}</b> в телеграм
                            </>
                        }
                    />
                </Form>
            </Modal>
        </>
    )
}

export default FinishRewind
